import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { MaintenanceService } from '@core/services/maintenance/maintenance.service';
import { environment } from '@environments/environment';
import { getDate } from '@fwmovilidad/qb-home-ita-components-lib';
import { COOKIE_PARTNERSHIP_ID, COOKIE_PARTNERSHIP_SHORT_NAME, RISKHOME } from '@global/constants';
import { ModifyPolicySkippingStepsEnum, PolicyActionEnum, VertiIntegrationFlow } from '@global/enums';
import { Flow, RoutePaths } from '@global/routePaths';
import { deleteSessionStorage, mapPolicyToProject } from '@global/utils';
import {
  HomePolicy,
  HomeProject,
  IntegrationTokenPayloadInfo,
  PartnershipType,
  Action as PremiumAction,
} from '@models/swagger';
import { Store, select } from '@ngrx/store';
import { JwtService } from '@services/jwt/jwt.service';
import { LoadingService } from '@services/loading/loading.service';
import { AppState } from '@store/app.state';
import { GetIntegrationsRequest } from '@store/configurator/getIntegrations';
import { GetPartnershipRequest } from '@store/configurator/getPartnership';
import { ConfiguratorService } from '@core/services/api/http/configurator.service';
import {
  EmptyFlowDataGeneral,
  IsBackOfficeUser,
  PartnershipIntegrationInd,
  UpdatePartnershipId,
  UpdatePolicyId,
  UpdatePolicyVersion,
  UpdateProjectId,
  UpdateRtkJwt,
  UpdateSessionId,
  UpdateTkJwt,
  UpdateUserId,
  UserFlow,
} from '@store/flow-data/general';
import { PolicySelectAction } from '@store/flow-data/policy';
import { UpdatePolicyCancellationReasonDesc } from '@store/flow-data/policy-cancellation';
import { ProjectClearData, ProjectUpdateNonModified, ProjectUpdateProject } from '@store/flow-data/project';
import { GetUserByIdRequest, ParticipantsRequest, selectGetUserByIdState } from '@store/swagger/applicationUtilities';
import {
  AlarmTypesRequest,
  GendersRequest,
  HomeRegimesRequest,
  HomeTypesRequest,
  HomeUsesRequest,
  RoadTypesRequest,
} from '@store/swagger/catalog';
import { GetPolicyRequest, PolicyActionsRequest, selectGetPolicyState, selectPolicyActionsState } from '@store/swagger/policy';
import {
  GetProjectByIdRequest,
  PostProjectRequest,
  selectGetProjectByIdState,
  selectPostComeroundProjectState,
  selectPostProjectState,
} from '@store/swagger/project';
import { PostComeroundProjectRequest } from '@store/swagger/project/postComeroundProject';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Observable, combineLatest, from } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';

dayjs.extend(customParseFormat);

@Injectable({
  providedIn: 'root',
})
export class PageGuard implements CanActivate {
  readonly TK = 'tk';
  readonly RTK = 'rtk';
  readonly PT_ID = 'ptId';

  readonly UTM_SOURCE = 'utm_source';
  readonly UTM_MEDIUM = 'utm_medium';
  readonly UTM_CAMPAIGN = 'utm_campaign';
  readonly UTM_TERM = 'utm_term';
  readonly UTM_CONTENT = 'utm_content';
  readonly GCLID = 'gclid';

  readonly MOCKED = 'mocked';

  private continueToErrorPage = false;
  private skipNavGuard = false;
  private continueToMaintenancePage = false;

  tk: string;
  rtk: string;
  ptId: string | null = null;
  allianceCode: string | null = null;

  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  gclid: string;

  userId: string;
  projectId: string;
  policyId: string;
  sessionId: string;
  action: IntegrationTokenPayloadInfo.ActionEnum;
  isBackOfficeUser: boolean;
  version: string;

  private cookies: Map<string, string>;

  constructor(
    private readonly router: Router,
    private readonly jwtService: JwtService,
    private readonly store: Store<AppState>,
    private readonly loadingService: LoadingService,
    private readonly analyticsService: AnalyticsService,
    private readonly maintenanceService: MaintenanceService,
    private readonly configuratorService: ConfiguratorService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    deleteSessionStorage(this.store);

    this.continueToErrorPage = this.router.getCurrentNavigation()?.extras?.state?.continueToErrorPage ?? false;
    this.continueToMaintenancePage =
      this.router.getCurrentNavigation()?.extras?.state?.continueToMaintenancePage ?? false;
    this.skipNavGuard = this.router.getCurrentNavigation()?.extras?.state?.skipNavGuard ?? false;

    if (environment.environmentName === 'DEV' && state.url === '/manutenzione') {
      this.continueToMaintenancePage = true;
    }

    return this.continueToErrorPage || this.skipNavGuard || this.continueToMaintenancePage
      ? this.loadingService
          .dismissLoading()
          .then(() => this.continueToErrorPage || this.skipNavGuard || this.continueToMaintenancePage)
      : this.checkData();
  }

  private checkData() {
    this.cookies = this.getCookies();

    return from(this.loadingService.presentLoading()).pipe(
      tap(() => {
        this.getParams();
        if (this.tk && this.rtk) {
          const jwtToken = this.jwtService.getTokenPayload(this.tk);
          this.userId = jwtToken.userId;
          this.projectId = jwtToken.projectId;
          this.policyId = jwtToken?.policy?.policyId;
          this.sessionId = jwtToken.sessionId;
          this.action = jwtToken.action;
          this.isBackOfficeUser = jwtToken.isBackOfficeUser;
          this.version = jwtToken.version;

          this.store.dispatch(UpdateTkJwt({ data: this.tk }));
          this.store.dispatch(UpdateRtkJwt({ data: this.rtk }));
          this.store.dispatch(UpdateUserId({ data: this.userId }));
          this.store.dispatch(UpdatePartnershipId({ data: this.ptId }));
          this.store.dispatch(UpdateProjectId({ data: this.projectId }));
          this.store.dispatch(UpdatePolicyId({ data: this.policyId }));
          this.store.dispatch(UpdatePolicyVersion({ data: this.version }));
          this.store.dispatch(IsBackOfficeUser({ data: this.isBackOfficeUser }));
        } else {
          // Not logged
          this.sessionId = crypto.randomUUID();
        }

        this.store.dispatch(UpdateSessionId({ data: this.sessionId }));
        this.setGlobalDigitalData();
      }),
      switchMap(() => this.redirectPage())
    );
  }

  private getParams(): void {
    const params = new URLSearchParams(window.location.search.slice(1));
    this.ptId = params.get(this.PT_ID) || this.cookies.get(COOKIE_PARTNERSHIP_ID) || null;
    this.allianceCode =
      params.get(COOKIE_PARTNERSHIP_SHORT_NAME) || this.cookies.get(COOKIE_PARTNERSHIP_SHORT_NAME) || null;

    this.tk = params.get(this.TK);
    this.rtk = params.get(this.RTK);

    this.utmSource = params.get(this.UTM_SOURCE);
    this.utmMedium = params.get(this.UTM_MEDIUM);
    this.utmCampaign = params.get(this.UTM_CAMPAIGN);
    this.utmTerm = params.get(this.UTM_TERM);
    this.utmContent = params.get(this.UTM_CONTENT);
    this.gclid = params.get(this.GCLID);

    this.store.dispatch(PartnershipIntegrationInd({ data: this.ptId !== null }));
    this.mocked(params);
  }

  // tslint:disable-next-line: cyclomatic-complexity
  private redirectPage() {
    if (this.maintenanceService.maintenanceInd) {
      return this.goToMaintenancePage();
    }

    this.preloadCatalogs();
    this.configuratorService.getGenesysChat().subscribe();

    if (this.ptId) {
      //this.store.dispatch(GetPartnershipRequest({ parameters: { partnershipId: this.ptId } }));
      // with partnership
      if (this.userId) {
        // logged
        if (this.projectId) {
          if (this.action === IntegrationTokenPayloadInfo.ActionEnum.ModifyProject) {
            // Modify quotation (logged) with partnership
            if (!environment.production) {
              if (!this.isBackOfficeUser) {
                console.log('Modify quotation (logged) with partnership');
              } else {
                console.log('Modify quotation (back office user) with partnership');
              }
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ModifyQuotationData }));
            return this.modifyQuotationWithPartnership();
          } else if (this.action === IntegrationTokenPayloadInfo.ActionEnum.ModifyCoverages) {
            // Modify coverages
            if (!environment.production) {
              console.log('Modify coverages');
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ModifyQuotationCoverages }));
            return this.modifyCoverages();
          } else {
            return this.goToErrorPage();
          }
        } else {
          // New quotation logged with partnership
          if (!environment.production) {
            if (!this.isBackOfficeUser) {
              console.log('New quotation logged with partnership');
            } else {
              console.log('New quotation as back office user with partnership');
            }
          }
          this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.NewQuotationLogged }));
          return this.newQuotationLoggedWithPartnership();
        }
      } else {
        // not logged
        // New quotation not logged with partnership
        if (!environment.production) {
          console.log('New quotation not logged with partnership');
        }
        this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.NewQuotationNotLogged }));
        return this.newQuotationNotLoggedWithPartnership();
      }
    } else {
      // without partnership
      if (this.userId) {
        // logged
        if (this.projectId) {
          if (this.action === IntegrationTokenPayloadInfo.ActionEnum.ModifyProject) {
            //  Modify quotation (logged) without partnership
            if (!environment.production) {
              if (!this.isBackOfficeUser) {
                console.log('Modify quotation (logged) without partnership');
              } else {
                console.log('Modify quotation (back office user) without partnership');
              }
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ModifyQuotationData }));
            return this.modifyQuotationWithoutPartnership();
          } else if (this.action === IntegrationTokenPayloadInfo.ActionEnum.ModifyCoverages) {
            // Modify coverages
            if (!environment.production) {
              if (!this.isBackOfficeUser) {
                console.log('Modify coverages');
              } else {
                console.log('Modify coverages as back office user');
              }
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ModifyQuotationCoverages }));
            return this.modifyCoverages();
          } else if (this.action === IntegrationTokenPayloadInfo.ActionEnum.ShortRenewal) {
            if (!environment.production) {
              if (!this.isBackOfficeUser) {
                console.log('Short Modify Renewal');
              } else {
                console.log('Short Modify Renewal as back office user');
              }
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ShortPolicyRenewal }));
            return this.shortRenewal();
          } else if (this.action === IntegrationTokenPayloadInfo.ActionEnum.CompleteRenewal) {
            if (!environment.production) {
              if (!this.isBackOfficeUser) {
                console.log('Complete Modify Renewal');
              } else {
                console.log('Complete Modify Renewal as back office user');
              }
            }
            this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.CompletePolicyRenewal }));
            return this.completeRenewal();
          } else {
            return this.goToErrorPage();
          }
        } else if (this.policyId) {
          // Modify policy - Post sale
          if (!environment.production) {
            if (!this.isBackOfficeUser) {
              console.log('Modify Policy');
            } else {
              console.log('Modify Policy as back office user');
            }
          }
          this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.ModifyPolicy }));
          return this.modifyPolicy();
        } else {
          // New quotation logged without partnership
          if (!environment.production) {
            if (!this.isBackOfficeUser) {
              console.log('New quotation logged without partnership');
            } else {
              console.log('New quotation as back office user without partnership');
            }
          }
          this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.NewQuotationLogged }));
          return this.newQuotationLoggedWithoutPartnership();
        }
      } else {
        // not logged
        // New quotation not logged without partnership
        if (!environment.production) {
          console.log('New quotation not logged without partnership');
        }
        this.store.dispatch(UserFlow({ data: VertiIntegrationFlow.NewQuotationNotLogged }));
        return this.newQuotationNotLoggedWithoutPartnership();
      }
    }
  }

  private modifyCoverages() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getProject(this.projectId, this.version);
    return combineLatest([
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectGetProjectByIdState)),
    ]).pipe(
      filter(userProjectState => userProjectState.every(({ loading }) => loading !== null && !loading)),
      take(1),
      switchMap(([userState, projectState]) => {
        if (userState.error || projectState.error) {
          return this.goToErrorPage();
        }
        this.store.dispatch(ProjectUpdateNonModified({ data: projectState.data }));

        const userPartnership = userState.data.partnershipCard ?? [];
        const projectPartnership = projectState.data.partnershipCard ?? [];

        const newProject: HomeProject = { ...projectState.data };
        const body: HomeProject = {
          ...newProject,
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([
            ...userPartnership,
            ...projectPartnership,
            { partnershipId: this.ptId, partnershipTypeCode: PartnershipType.Cookie },
          ]),
        };

        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }

            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                date: body.rateFromDate ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();

            this.getProject(data.projectQuoteId, data.version);
            return this.store.pipe(select(selectGetProjectByIdState)).pipe(
              filter(({ loading }) => loading !== null && !loading),
              take(1),
              switchMap(({ error, data }) => {
                if (error) {
                  return this.goToErrorPage();
                }
                return this.navigateRoute(RoutePaths.MODIFY_QUOTATION.POLICY_DATA.COVERAGES.route, {
                  flow: Flow.MODIFY_QUOTATION,
                  modifyIntegrationAction: this.action,
                });
              })
            );
          })
        );
      })
    );
  }

  private shortRenewal() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getProject(this.projectId, this.version);
    return combineLatest([
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectGetProjectByIdState)),
    ]).pipe(
      filter(userProjectState => userProjectState.every(({ loading }) => loading !== null && !loading)),
      take(1),
      switchMap(([userState, projectState]) => {
        if (userState.error || projectState.error) {
          return this.goToErrorPage();
        }
        this.store.dispatch(ProjectUpdateNonModified({ data: projectState.data }));

        const userPartnership = userState.data.partnershipCard ?? [];
        const projectPartnership = projectState.data.partnershipCard ?? [];

        const newProject: HomeProject = { ...projectState.data };
        const body: HomeProject = {
          ...newProject,
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([...userPartnership, ...projectPartnership]),
        };

        this.configuratorService
          .getAllianceAffinityConfiguration({
            allianceCode: this.allianceCode ?? undefined,
            affinityCode: this.ptId ?? undefined,
            date: body.rateFromDate ?? undefined,
            risk: RISKHOME,
          })
          .subscribe();

        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }

            this.getProject(data.projectQuoteId, data.version);
            return this.store.pipe(select(selectGetProjectByIdState)).pipe(
              filter(({ loading }) => loading !== null && !loading),
              take(1),
              switchMap(({ error, data }) => {
                if (error) {
                  return this.goToErrorPage();
                }
                return this.navigateRoute(RoutePaths.MODIFY_RENEWAL.RENEWAL_DATA.COVERAGES_RENEWAL.route, {
                  flow: Flow.MODIFY_RENEWAL,
                  modifyIntegrationAction: this.action,
                });
              })
            );
          })
        );
      })
    );
  }

  private completeRenewal() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getProject(this.projectId, this.version);
    return combineLatest([
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectGetProjectByIdState)),
    ]).pipe(
      filter(userProjectState => userProjectState.every(({ loading }) => loading !== null && !loading)),
      take(1),
      switchMap(([userState, projectState]) => {
        if (userState.error || projectState.error) {
          return this.goToErrorPage();
        }
        this.store.dispatch(ProjectUpdateNonModified({ data: projectState.data }));

        const userPartnership = userState.data.partnershipCard ?? [];
        const projectPartnership = projectState.data.partnershipCard ?? [];

        const newProject: HomeProject = { ...projectState.data };

        const body: HomeProject = {
          ...newProject,
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([...userPartnership, ...projectPartnership]),
        };
        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }
            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                date: body.rateFromDate ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();

            this.getProject(data.projectQuoteId, data.version);
            return this.store.pipe(select(selectGetProjectByIdState)).pipe(
              filter(({ loading }) => loading !== null && !loading),
              take(1),
              switchMap(({ error, data }) => {
                if (error) {
                  return this.goToErrorPage();
                }
                return this.navigateRoute(RoutePaths.MODIFY_RENEWAL.RENEWAL_DATA.POLICY_RENEWAL_SUMMARY.route, {
                  flow: Flow.MODIFY_RENEWAL,
                  modifyIntegrationAction: this.action,
                });
              })
            );
          })
        );
      })
    );
  }

  private modifyPolicy() {
    this.getPolicy(this.policyId, this.version);
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getPolicyActions(this.policyId);
    return combineLatest([
      this.store.pipe(select(selectGetPolicyState)),
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectPolicyActionsState)),
    ]).pipe(
      filter(policyUserState => policyUserState.every(({ loading }) => loading !== null && !loading)),
      take(1),
      switchMap(([policyState, userState, policyActionsState]) => {
        if (policyState.error || userState.error || policyActionsState.error) {
          return this.goToErrorPage();
        }
        this.configuratorService
          .getAllianceAffinityConfiguration({
            allianceCode: this.allianceCode ?? undefined,
            affinityCode: this.ptId ?? undefined,
            date: policyState.data.rateFromDate ?? undefined,
            risk: RISKHOME,
          })
          .subscribe();
        return this.modifyPolicyNavigation(policyState?.data, policyActionsState?.data);
      })
    );
  }

  private modifyPolicyNavigation(policy: HomePolicy, policyActions: PremiumAction[]) {
    const policyActionsAvailable: PremiumAction[] = policyActions?.filter(action => action.allowedActionInd);
    if (policyActionsAvailable?.length === 1) {
      this.store.dispatch(PolicySelectAction({ data: policyActionsAvailable[0] }));
      const policySubActionsAvailable = policyActionsAvailable[0].associatedActions?.filter(
        action => action.allowedActionInd
      );
      if (policySubActionsAvailable?.length === 1) {
        return this.modifyPolicyNavigationWithOneSubAction(policy, policyActionsAvailable, policySubActionsAvailable);
      } else {
        return this.modifyPolicyNavigationWithMoreThanOneSubAction(policyActionsAvailable);
      }
    } else {
      return this.navigateRoute(RoutePaths.MODIFY_POLICY.route, {
        flow: Flow.MODIFY_POLICY,
      });
    }
  }

  private modifyPolicyNavigationWithMoreThanOneSubAction(policyActionsAvailable: PremiumAction[]) {
    if (policyActionsAvailable[0].actionId === PolicyActionEnum.CANCEL) {
      return this.navigateRoute(RoutePaths.MODIFY_POLICY.CANCEL.REASONS.route, {
        modifyPolicySkippingSteps: ModifyPolicySkippingStepsEnum.ACTIONS,
        action: PolicyActionEnum.CANCEL,
        flow: Flow.MODIFY_POLICY,
      });
    } else if (policyActionsAvailable[0].actionId === PolicyActionEnum.EDIT) {
      return this.navigateRoute(RoutePaths.MODIFY_POLICY.ACTIONS.route, {
        modifyPolicySkippingSteps: ModifyPolicySkippingStepsEnum.ACTIONS,
        action: PolicyActionEnum.EDIT,
        flow: Flow.MODIFY_POLICY,
      });
    } else {
      return this.goToErrorPage();
    }
  }

  private modifyPolicyNavigationWithOneSubAction(
    policy: HomePolicy,
    policyActionsAvailable: PremiumAction[],
    policySubActionsAvailable: PremiumAction[]
  ) {
    if (policyActionsAvailable[0].actionId === PolicyActionEnum.CANCEL) {
      const homeProject = mapPolicyToProject(policy, {}, { userId: this.userId });
      homeProject.actionId = policySubActionsAvailable[0].actionId;
      homeProject.policyCancellationDate = getDate();
      this.store.dispatch(
        UpdatePolicyCancellationReasonDesc({
          data: policySubActionsAvailable[0]?.actionName,
        })
      );
      this.store.dispatch(ProjectUpdateProject({ data: homeProject }));
      this.store.dispatch(PostProjectRequest({ body: homeProject }));
      return this.navigateRoute(RoutePaths.MODIFY_POLICY.CANCEL.CONDITIONS.route, {
        modifyPolicySkippingSteps: ModifyPolicySkippingStepsEnum.SUBACTIONS,
        action: PolicyActionEnum.CANCEL,
        flow: Flow.MODIFY_POLICY,
      });
    } else if (policyActionsAvailable[0].actionId === PolicyActionEnum.EDIT) {
      return this.navigateRoute(RoutePaths.MODIFY_POLICY.ACTIONS.route, {
        modifyPolicySkippingSteps: ModifyPolicySkippingStepsEnum.SUBACTIONS,
        action: PolicyActionEnum.EDIT,
        flow: Flow.MODIFY_POLICY,
      });
    } else {
      return this.goToErrorPage();
    }
  }

  private modifyQuotationWithoutPartnership() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getProject(this.projectId, this.version);
    return combineLatest([
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectGetProjectByIdState)),
    ]).pipe(
      filter(userProjectState => userProjectState.every(({ loading }) => loading !== null && !loading)),
      take(1),
      switchMap(([userState, projectState]) => {
        if (userState.error || projectState.error) {
          return this.goToErrorPage();
        }
        this.store.dispatch(ProjectUpdateNonModified({ data: projectState.data }));

        const userPartnership = userState.data.partnershipCard ?? [];
        const projectPartnership = projectState.data.partnershipCard ?? [];

        const newProject: HomeProject = { ...projectState.data };

        const body: HomeProject = {
          ...newProject,
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([...userPartnership, ...projectPartnership]),
        };
        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }
            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                date: body.rateFromDate ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();

            this.getProject(data.projectQuoteId, data.version);
            return this.store.pipe(select(selectGetProjectByIdState)).pipe(
              filter(({ loading }) => loading !== null && !loading),
              take(1),
              switchMap(({ error, data }) => {
                if (error) {
                  return this.goToErrorPage();
                }
                return this.navigateRoute(RoutePaths.MODIFY_QUOTATION.PROJECT_SUMMARY.route, {
                  flow: Flow.MODIFY_QUOTATION,
                  modifyIntegrationAction: this.action,
                  projectPastEffectiveDateInd: this.isPastDate(projectState.data?.effectiveDate),
                });
              })
            );
          })
        );
      })
    );
  }

  private newQuotationNotLoggedWithPartnership() {
    const body: HomeProject = {
      partnershipCard: [{ partnershipId: this.ptId, partnershipTypeCode: PartnershipType.Cookie }],
    };
    this.store.dispatch(PostComeroundProjectRequest({ body }));
    this.store.dispatch(UpdatePartnershipId({ data: this.ptId }));
    return this.store.pipe(select(selectPostComeroundProjectState)).pipe(
      filter(({ loading }) => loading !== null && !loading),
      take(1),
      switchMap(({ error }) => {
        if (error) {
          return this.goToErrorPage();
        }

        this.configuratorService
          .getAllianceAffinityConfiguration({
            allianceCode: this.allianceCode ?? undefined,
            affinityCode: this.ptId ?? undefined,
            risk: RISKHOME,
          })
          .subscribe();
        return this.navigateRoute(RoutePaths.NEW_QUOTATION.HOME_DATA.ZIP_CODE.route, {
          flow: Flow.NEW_QUOTATION,
        });
      })
    );
  }

  private newQuotationLoggedWithoutPartnership() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    return this.store.pipe(select(selectGetUserByIdState)).pipe(
      filter(({ loading }) => loading !== null && !loading),
      take(1),
      switchMap(({ error, data }) => {
        if (error) {
          return this.goToErrorPage();
        }
        const userPartnership = data.partnershipCard ?? [];
        const body: HomeProject = {
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([...userPartnership]),
        };
        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }
            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();

            return this.navigateRoute(RoutePaths.NEW_QUOTATION.HOME_DATA.ZIP_CODE.route, {
              flow: Flow.NEW_QUOTATION,
            });
          })
        );
      })
    );
  }

  private newQuotationLoggedWithPartnership() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    return this.store.pipe(select(selectGetUserByIdState)).pipe(
      filter(({ loading }) => loading !== null && !loading),
      take(1),
      switchMap(({ error, data }) => {
        if (error) {
          return this.goToErrorPage();
        }
        const userPartnership = data.partnershipCard ?? [];
        const body: HomeProject = {
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([
            ...userPartnership,
            { partnershipId: this.ptId, partnershipTypeCode: PartnershipType.Cookie },
          ]),
        };
        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();
            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));
            return this.navigateRoute(RoutePaths.NEW_QUOTATION.HOME_DATA.ZIP_CODE.route, {
              flow: Flow.NEW_QUOTATION,
            });
          })
        );
      })
    );
  }

  private modifyQuotationWithPartnership() {
    this.getUser(this.userId);
    this.getUserParticipants(this.userId);
    this.getProject(this.projectId, this.version);
    return combineLatest([
      this.store.pipe(select(selectGetUserByIdState)),
      this.store.pipe(select(selectGetProjectByIdState)),
    ]).pipe(
      filter(([{ loading: userLoading }, { loading: projectLoading }]) => !userLoading && !projectLoading),
      take(1),
      switchMap(([userState, projectState]) => {
        if (userState.error || projectState.error) {
          return this.goToErrorPage();
        }
        this.store.dispatch(ProjectUpdateNonModified({ data: projectState.data }));

        const userPartnership = userState.data.partnershipCard ?? [];
        const projectPartnership = projectState.data.partnershipCard ?? [];

        const newProject: HomeProject = { ...projectState.data };
        const body: HomeProject = {
          ...newProject,
          userId: this.userId,
          partnershipCard: this.getUndefinedIfEmptyList([
            ...userPartnership,
            ...projectPartnership,
            { partnershipId: this.ptId, partnershipTypeCode: PartnershipType.Cookie },
          ]),
        };
        this.store.dispatch(PostProjectRequest({ body }));
        return this.store.pipe(select(selectPostProjectState)).pipe(
          filter(({ loading }) => loading !== null && !loading),
          take(1),
          switchMap(({ error, data }) => {
            if (error) {
              return this.goToErrorPage();
            }
            const bestPartnership = data.partnershipCard?.find(
              partnership => partnership.partnershipTypeCode === PartnershipType.Project
            );
            this.store.dispatch(UpdatePartnershipId({ data: bestPartnership?.partnershipId }));

            this.configuratorService
              .getAllianceAffinityConfiguration({
                allianceCode: this.allianceCode ?? undefined,
                affinityCode: this.ptId ?? undefined,
                date: body.rateFromDate ?? undefined,
                risk: RISKHOME,
              })
              .subscribe();

            this.getProject(data.projectQuoteId, data.version);
            return this.store.pipe(select(selectGetProjectByIdState)).pipe(
              filter(({ loading }) => loading !== null && !loading),
              take(1),
              switchMap(({ error, data }) => {
                if (error) {
                  return this.goToErrorPage();
                }
                return this.navigateRoute(RoutePaths.MODIFY_QUOTATION.PROJECT_SUMMARY.route, {
                  flow: Flow.MODIFY_QUOTATION,
                  modifyIntegrationAction: this.action,
                  projectPastEffectiveDateInd: this.isPastDate(projectState.data?.effectiveDate),
                });
              })
            );
          })
        );
      })
    );
  }

  private newQuotationNotLoggedWithoutPartnership() {
    const body: HomeProject = this.userId ? { userId: this.userId } : {};
    this.store.dispatch(PostComeroundProjectRequest({ body }));
    return this.store.pipe(select(selectPostComeroundProjectState)).pipe(
      filter(({ loading }) => loading !== null && !loading),
      take(1),
      switchMap(({ error }) => {
        if (error) {
          return this.goToErrorPage();
        }

        this.configuratorService
          .getAllianceAffinityConfiguration({
            allianceCode: this.allianceCode ?? undefined,
            affinityCode: this.ptId ?? undefined,
            risk: RISKHOME,
          })
          .subscribe();

        return this.navigateRoute(RoutePaths.NEW_QUOTATION.HOME_DATA.ZIP_CODE.route, {
          flow: Flow.NEW_QUOTATION,
        });
      })
    );
  }

  private preloadCatalogs() {
    // Preload integrations
    this.store.dispatch(GetIntegrationsRequest({}));
    // homeTypes
    this.store.dispatch(HomeTypesRequest({}));
    // alarmTypes
    this.store.dispatch(AlarmTypesRequest({}));
    // homeRegimes
    this.store.dispatch(HomeRegimesRequest({}));
    // homeUses
    this.store.dispatch(HomeUsesRequest({}));
    // genders
    this.store.dispatch(GendersRequest({}));
    // roadTypes
    this.store.dispatch(RoadTypesRequest({}));
  }

  private getProject(projectId: string, version: string) {
    this.store.dispatch(GetProjectByIdRequest({ parameters: { projectQuoteId: projectId, version } }));
  }

  private getPolicy(policyId: string, policyVersion: string) {
    this.store.dispatch(GetPolicyRequest({ parameters: { policyId, policyVersion } }));
  }

  private getPolicyActions(policyId: string) {
    this.store.dispatch(PolicyActionsRequest({ parameters: { policyId } }));
  }

  private getUser(userId: string) {
    this.store.dispatch(GetUserByIdRequest({ parameters: { userId } }));
  }

  private getUserParticipants(userId: string) {
    this.store.dispatch(ParticipantsRequest({ parameters: { userId } }));
  }

  // tslint:disable-next-line: no-any
  private navigateRoute(route: string, payload?: Record<string, any>): Promise<boolean> {
    return this.router
      .navigateByUrl(route, { state: { ...payload, allowNavigation: true } })
      .finally(() => this.loadingService.dismissLoading())
      .then(() => false);
  }

  private goToErrorPage() {
    return this.navigateRoute(RoutePaths.GENERIC_ERROR.route, { continueToErrorPage: true });
  }

  private goToMaintenancePage() {
    return this.navigateRoute(RoutePaths.MAINTENANCE_PAGE.route, { continueToMaintenancePage: true });
  }

  private getCookies(): Map<string, string> {
    const COOKIE_SEPARATOR = ';';
    const COOKIE_VALUE_SEPARATOR = '=';

    const rawList = document.cookie.split(COOKIE_SEPARATOR);
    const cookiePairs = rawList.map(cookiePair => cookiePair.split(new RegExp(`${COOKIE_VALUE_SEPARATOR}(.*)`, 's')));
    return cookiePairs.reduce((prev, [key, value]) => {
      prev.set(key.trim(), value);
      return prev;
    }, new Map<string, string>());
  }

  private setGlobalDigitalData() {
    this.analyticsService.setUtmSource(this.utmSource);
    this.analyticsService.setUtmMedium(this.utmMedium);
    this.analyticsService.setUtmCampaign(this.utmCampaign);
    this.analyticsService.setUtmTerm(this.utmTerm);
    this.analyticsService.setUtmContent(this.utmContent);
    this.analyticsService.setGclid(this.gclid);
    this.analyticsService.setPartnership(this.ptId);
    this.analyticsService.setClientUserId(this.userId);
  }
  // TODO Delete
  private mocked(params: URLSearchParams) {
    /* START MOCKED DATA -> USE ONY IN DEV */
    /*
      {
        "customPayload": {
        "userId": "1018728645",
        "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY"
        }
      }
    */
    const NEW_QUOTATION_LOGGED_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIn19.244Svqt0J1uHTEVlOM0RVPw2K0on-jGqKCy9bUTI7Z8';
    const NEW_QUOTATION_LOGGED_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIn19.244Svqt0J1uHTEVlOM0RVPw2K0on-jGqKCy9bUTI7Z8';
    /*
      {
        "customPayload": {
        "userId": "1018728645",
        "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
        "isBackOfficeUser": true
        }
      }
    */
    const NEW_QUOTATION_LOGGED_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwiaXNCYWNrT2ZmaWNlVXNlciI6dHJ1ZX19.VCoT5nfcEjbiwfGhBWb8icWVKFMQxBHpNRbeAXHeSN0';
    const NEW_QUOTATION_LOGGED_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwiaXNCYWNrT2ZmaWNlVXNlciI6dHJ1ZX19.VCoT5nfcEjbiwfGhBWb8icWVKFMQxBHpNRbeAXHeSN0';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "modify_project"
        }
      }
    */
    const MODIFY_QUOTATION_DATA_LOGGED_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9wcm9qZWN0In19.SuNnjfRDf-8zF6Ap07I4MbTJbzbi841p1ZJOYPX9goU';
    const MODIFY_QUOTATION_DATA_LOGGED_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9wcm9qZWN0In19.SuNnjfRDf-8zF6Ap07I4MbTJbzbi841p1ZJOYPX9goU';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "modify_project",
          "isBackOfficeUser": true
        }
      }
    */
    const MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9wcm9qZWN0IiwiaXNCYWNrT2ZmaWNlVXNlciI6dHJ1ZX19.0ZcHvusae_7WQA2kE3FJ4uWbJW4S3dPqWtcWgfrXa7M';
    const MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9wcm9qZWN0IiwiaXNCYWNrT2ZmaWNlVXNlciI6dHJ1ZX19.0ZcHvusae_7WQA2kE3FJ4uWbJW4S3dPqWtcWgfrXa7M';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "modify_coverages"
        }
      }
    */
    const MODIFY_QUOTATION_COVERAGES_LOGGED_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9jb3ZlcmFnZXMifX0.GGpRGuACnvVanSU_eEHRJh3k8VwR0ljQoalUNTwrRN8';
    const MODIFY_QUOTATION_COVERAGES_LOGGED_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9jb3ZlcmFnZXMifX0.GGpRGuACnvVanSU_eEHRJh3k8VwR0ljQoalUNTwrRN8';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "modify_coverages",
          "isBackOfficeUser": true
        }
      }
    */
    const MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9jb3ZlcmFnZXMiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.k4VydC2WXuHBn0YtnfRqm2p-KWIdeyrxPqC_7FsrHLI';
    const MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6Im1vZGlmeV9jb3ZlcmFnZXMiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.k4VydC2WXuHBn0YtnfRqm2p-KWIdeyrxPqC_7FsrHLI';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "policy": {
            "policyId": "HOM0946400367"
          }
        }
      }
    */
    const MODIFY_POLICY_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwicG9saWN5Ijp7InBvbGljeUlkIjoiSE9NMDk0NjQwMDM2NyJ9fX0.4UB7cR75rSPnSjmPTR44TO-_yiQmhpKnPFWDpWeLJQA';
    const MODIFY_POLICY_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwicG9saWN5Ijp7InBvbGljeUlkIjoiSE9NMDk0NjQwMDM2NyJ9fX0.4UB7cR75rSPnSjmPTR44TO-_yiQmhpKnPFWDpWeLJQA';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "policy": {
            "policyId": "HOM0946400367"
          },
          "isBackOfficeUser": true
        }
      }
    */
    const MODIFY_POLICY_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwicG9saWN5Ijp7InBvbGljeUlkIjoiSE9NMDk0NjQwMDM2NyJ9LCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.lT2ylxOg0I3n0eL_karUS7v8ImcOZvSN1sfPB7qSNuI';
    const MODIFY_POLICY_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJzZXNzaW9uSWQiOiJ4OExDRTNqdGlIMWVzc1IxbmtZMk9jeU9ZIiwicG9saWN5Ijp7InBvbGljeUlkIjoiSE9NMDk0NjQwMDM2NyJ9LCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.lT2ylxOg0I3n0eL_karUS7v8ImcOZvSN1sfPB7qSNuI';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "short_renewal"
        }
      }
    */
    const MODIFY_RENEWAL_SHORT_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6InNob3J0X3JlbmV3YWwifX0.y2gboNZPecl6inUOt-iGzu_vNz46w8MO8AOVVF49lfg';
    const MODIFY_RENEWAL_SHORT_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6InNob3J0X3JlbmV3YWwifX0.y2gboNZPecl6inUOt-iGzu_vNz46w8MO8AOVVF49lfg';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "short_renewal",
          "isBackOfficeUser": true
        }
      }
    */
    const MODIFY_RENEWAL_SHORT_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6InNob3J0X3JlbmV3YWwiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.SrqXo8B896gCWLVACeRUloAap6XR8bEaNPQDQeo1RCE';
    const MODIFY_RENEWAL_SHORT_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6InNob3J0X3JlbmV3YWwiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.SrqXo8B896gCWLVACeRUloAap6XR8bEaNPQDQeo1RCE';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "complete_renewal"
        }
      }
    */
    const MODIFY_RENEWAL_COMPLETE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6ImNvbXBsZXRlX3JlbmV3YWwifX0.UCIUJsvfXzbPUzRmmxRgEVWjjPvn8PKaYyz4wb-545w';
    const MODIFY_RENEWAL_COMPLETE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6ImNvbXBsZXRlX3JlbmV3YWwifX0.UCIUJsvfXzbPUzRmmxRgEVWjjPvn8PKaYyz4wb-545w';
    /*
      {
        "customPayload": {
          "userId": "1018728645",
          "projectId": "PRH0475355358",
          "sessionId": "x8LCE3jtiH1essR1nkY2OcyOY",
          "action": "complete_renewal",
          "isBackOfficeUser": true
        }
      }
    */
    const MODIFY_RENEWAL_COMPLETE_BACKOFFICE_TK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6ImNvbXBsZXRlX3JlbmV3YWwiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.4nCdsKYw7aYCV-cEZFiOD-SvDHRUYpT81Z9FNR-bPUg';
    const MODIFY_RENEWAL_COMPLETE_BACKOFFICE_RTK =
      'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjdXN0b21QYXlsb2FkIjp7InVzZXJJZCI6IjEwMTg3Mjg2NDUiLCJwcm9qZWN0SWQiOiJQUkgwNDc1MzU1MzU4Iiwic2Vzc2lvbklkIjoieDhMQ0UzanRpSDFlc3NSMW5rWTJPY3lPWSIsImFjdGlvbiI6ImNvbXBsZXRlX3JlbmV3YWwiLCJpc0JhY2tPZmZpY2VVc2VyIjp0cnVlfX0.4nCdsKYw7aYCV-cEZFiOD-SvDHRUYpT81Z9FNR-bPUg';

    /* END MOCKED DATA */

    const NEW_QUOTATION_LOGGED = 'new_quotation_logged';
    const NEW_QUOTATION_LOGGED_BACKOFFICE = 'new_quotation_logged_backoffice';
    const MODIFY_QUOTATION_DATA_LOGGED = 'modify_quotation_data_logged';
    const MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE = 'modify_quotation_data_logged_backoffice';
    const MODIFY_QUOTATION_COVERAGES_LOGGED = 'modify_quotation_coverages_logged';
    const MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE = 'modify_quotation_coverages_logged_backoffice';
    const MODIFY_POLICY = 'modify_policy';
    const MODIFY_POLICY_BACKOFFICE = 'modify_policy_backoffice';
    const MODIFY_RENEWAL_SHORT = 'modify_renewal_short';
    const MODIFY_RENEWAL_SHORT_BACKOFFICE = 'modify_renewal_short_backoffice';
    const MODIFY_RENEWAL_COMPLETE = 'modify_renewal_complete';
    const MODIFY_RENEWAL_COMPLETE_BACKOFFICE = 'modify_renewal_complete_backoffice';

    const mocked = params.get(this.MOCKED);

    if (mocked) {
      switch (mocked) {
        case NEW_QUOTATION_LOGGED:
          this.tk = NEW_QUOTATION_LOGGED_TK;
          this.rtk = NEW_QUOTATION_LOGGED_RTK;
          break;
        case NEW_QUOTATION_LOGGED_BACKOFFICE:
          this.tk = NEW_QUOTATION_LOGGED_BACKOFFICE_TK;
          this.rtk = NEW_QUOTATION_LOGGED_BACKOFFICE_RTK;
          break;
        case MODIFY_QUOTATION_DATA_LOGGED:
          this.tk = MODIFY_QUOTATION_DATA_LOGGED_TK;
          this.rtk = MODIFY_QUOTATION_DATA_LOGGED_RTK;
          break;
        case MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE:
          this.tk = MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE_TK;
          this.rtk = MODIFY_QUOTATION_DATA_LOGGED_BACKOFFICE_RTK;
          break;
        case MODIFY_QUOTATION_COVERAGES_LOGGED:
          this.tk = MODIFY_QUOTATION_COVERAGES_LOGGED_TK;
          this.rtk = MODIFY_QUOTATION_COVERAGES_LOGGED_RTK;
          break;
        case MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE:
          this.tk = MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE_TK;
          this.rtk = MODIFY_QUOTATION_COVERAGES_LOGGED_BACKOFFICE_RTK;
          break;
        case MODIFY_POLICY:
          this.tk = MODIFY_POLICY_TK;
          this.rtk = MODIFY_POLICY_RTK;
          break;
        case MODIFY_POLICY_BACKOFFICE:
          this.tk = MODIFY_POLICY_BACKOFFICE_TK;
          this.rtk = MODIFY_POLICY_BACKOFFICE_RTK;
          break;
        case MODIFY_RENEWAL_SHORT:
          this.tk = MODIFY_RENEWAL_SHORT_TK;
          this.rtk = MODIFY_RENEWAL_SHORT_RTK;
          break;
        case MODIFY_RENEWAL_SHORT_BACKOFFICE:
          this.tk = MODIFY_RENEWAL_SHORT_BACKOFFICE_TK;
          this.rtk = MODIFY_RENEWAL_SHORT_BACKOFFICE_RTK;
          break;
        case MODIFY_RENEWAL_COMPLETE:
          this.tk = MODIFY_RENEWAL_COMPLETE_TK;
          this.rtk = MODIFY_RENEWAL_COMPLETE_RTK;
          break;
        case MODIFY_RENEWAL_COMPLETE_BACKOFFICE:
          this.tk = MODIFY_RENEWAL_COMPLETE_BACKOFFICE_TK;
          this.rtk = MODIFY_RENEWAL_COMPLETE_BACKOFFICE_RTK;
          break;
        default:
          this.tk = undefined;
          this.rtk = undefined;
          break;
      }
    }
  }

  private getUndefinedIfEmptyList<T>(list: T[]): T[] | undefined {
    return list?.length > 0 ? list : undefined;
  }

  private isPastDate(date: Date) {
    const currentDate = dayjs().set('hours', 0).set('minutes', 0).set('seconds', 0).set('milliseconds', 0);

    const pastDate = dayjs(date).set('hours', 0).set('minutes', 0).set('seconds', 0).set('milliseconds', 0);
    return pastDate.diff(currentDate) < 0;
  }
}
